import { FormikValues, useFormikContext } from "formik";
import React from "react";
import AsyncSearchField from "shared/form/fields/asyncSearch";
import { asyncOrganizationFn, useGetOrganizationDetailsForRegistration } from "./dataloader";
import FieldIdentifier from "shared/form/renderer/enums/FieldIdentifier";
import { useDynamicFormRenderer } from "shared/form/renderer/context/dynamicFormRendererContext";
import DynamicFieldConfigType from "shared/form/renderer/types/DynamicFieldConfigType";
import OrganizationStatus from "shared/enums/OrganizationStatus";
import { useCopies } from "shared/hooks/useCopies";
import SchemaService from "../../SchemaService";
import { SchemaField } from "../../types/SchemaField";
import DynamicFieldLabel from "../DynamicFieldLabel";
import { ORGANIZATION_NAME_RESET_DELAY_TIME } from "shared/utils/constants";
import { OrganizationNameAdditionalLabelProps } from "./type";

export default function OrganizationName(props: DynamicFieldConfigType) {
  const {
    registrationFormId,
    eventSeriesId,
    orgChangeHandler,
    isAdminOnly,
    organizationId,
    organizationStatus,
    isRegistrationOrganization,
    organizationEditService
  } = useDynamicFormRenderer();

  const { setValues, values } = useFormikContext<FormikValues>();
  const [selectedOrganizationId, setSelectedOrganizationId] = React.useState<string>();
  const [currentOrganizationStatus, setCurrentOrganizationStatus] = React.useState<OrganizationStatus | undefined>(
    organizationStatus
  );

  // to store the set of keys that were updated when reg org details were received
  const updatedKeysRef = React.useRef<Array<SchemaField>>([]);
  const { data: registrationOrganizationDetails } = useGetOrganizationDetailsForRegistration({
    registrationFormId,
    organizationId: selectedOrganizationId,
    isAdminOnly
  });

  React.useEffect(() => {
    if (registrationOrganizationDetails?.profileFields.length) {
      setValues(prevValues => ({
        ...prevValues,
        ...SchemaService.getInitialValue(registrationOrganizationDetails?.profileFields),
        [FieldIdentifier.ORGANIZATION_ID]: selectedOrganizationId
      }));
      // store the keys for which we updated the values in our form
      updatedKeysRef.current = registrationOrganizationDetails?.profileFields.map(profileField => ({
        ...profileField,
        defaultValue: null,
        value: null
      }));
    }
  }, [registrationOrganizationDetails, selectedOrganizationId, setValues]);

  // method will return the body to be sent in api
  const getRequestBody = React.useCallback(
    (name: string) => ({
      name,
      eventSeriesId,
      resultCount: 10
    }),
    [eventSeriesId]
  );

  // callback method that will receive the selected organization
  const optionSelectedCallback = React.useCallback(
    (obj, organizationList) => {
      if (isAdminOnly) {
        // setOrgStatus(OrganizationStatus.ACTIVE);
        // pass the selected organization id to org change handler
        // orgChangeHandler will call the api and get the schema fields based on org id and registration form type
        orgChangeHandler?.(obj.value);
      } else if (isRegistrationOrganization) {
        //need selected organization detail for registration organization
        orgChangeHandler?.(
          obj.value,
          organizationList.find((d: any) => d.id === obj.value)
        );
      } else {
        // when used in registration portal:
        //    we'll make the api call and get reg org details and set values in form
        setSelectedOrganizationId(obj.value);
      }
    },
    [isAdminOnly, isRegistrationOrganization, orgChangeHandler]
  );

  // callback method called whenever an org name is deselected/edited
  const resetCallback = React.useCallback(() => {
    // what to do when org is edited ie org is unselected
    // for registration portal we need to clear all the fields that we had set initially using reg org details
    if (!isAdminOnly && updatedKeysRef.current.length) {
      setValues(prevValues => ({
        ...prevValues,
        ...SchemaService.getInitialValue(updatedKeysRef.current),
        [FieldIdentifier.ORGANIZATION_ID]: ""
      }));
      // need to do following else for same organization our useEffect to set reg org dtls won't run
      setSelectedOrganizationId(undefined);
    } else if (isAdminOnly) {
      setValues(prevValues => ({ ...prevValues, [FieldIdentifier.ORGANIZATION_ID]: "" }));
      setCurrentOrganizationStatus(undefined);
    } else if (!isAdminOnly) {
      setValues(prevValues => ({ ...prevValues, [FieldIdentifier.ORGANIZATION_ID]: "" }));
    }
  }, [isAdminOnly, setValues]);

  const alreadySelectedOption = React.useMemo(
    () => ({ label: values[props.schemaObj.id], value: organizationId || "" }),
    [organizationId, props.schemaObj.id, values]
  );

  const resetCallbackHandler = React.useCallback(() => {
    if (isAdminOnly) {
      // Screen freezes on participant modal screen when user selects organization name and types. Added delay as workaround for this issue.
      setTimeout(resetCallback, ORGANIZATION_NAME_RESET_DELAY_TIME);
    } else {
      resetCallback();
    }
  }, [isAdminOnly, resetCallback]);

  const editHandler = React.useMemo(() => {
    if (!props.schemaObj.disabled && organizationEditService && !organizationEditService?.isEditable) {
      return organizationEditService?.makeEditableHandler;
    }
  }, [organizationEditService, props.schemaObj.disabled]);

  const isDisabled = React.useMemo(() => {
    if (organizationEditService) {
      return props.schemaObj.disabled || !organizationEditService.isEditable;
    }
    return props.schemaObj.disabled;
  }, [organizationEditService, props.schemaObj.disabled]);

  const displayOrganizationStatusLabel = React.useMemo(
    () => isAdminOnly && [OrganizationStatus.PENDING, undefined].includes(currentOrganizationStatus),
    [currentOrganizationStatus, isAdminOnly]
  );

  return (
    <React.Fragment>
      <AsyncSearchField
        name={props.schemaObj.id}
        label={<DynamicFieldLabel schemaObj={props.schemaObj} editHandler={editHandler} />}
        apiWrapper={asyncOrganizationFn}
        hideLoader={true}
        required={props.schemaObj.mandatory}
        validate={props.validationFn}
        getRequestBody={getRequestBody}
        disabled={isDisabled}
        optionSelectedCallback={optionSelectedCallback}
        resetCallback={resetCallbackHandler}
        additionalLabel={
          displayOrganizationStatusLabel ? (
            <OrganizationNameAdditionalLabel currentOrganizationStatus={currentOrganizationStatus} />
          ) : null
        }
        alreadySelectedOption={alreadySelectedOption}
        fullWidth={props.schemaObj.fullWidthView}
        indented={props.schemaObj.indented}
      />
    </React.Fragment>
  );
}

function OrganizationNameAdditionalLabel(props: OrganizationNameAdditionalLabelProps) {
  const copies = useCopies();

  return (
    <React.Fragment>
      {props.currentOrganizationStatus === OrganizationStatus.PENDING && (
        <span className="organizationName_pendingOrganization">({copies.get("new")})</span>
      )}
      {props.currentOrganizationStatus === undefined && (
        <span className="organizationName_selectOrganization">({copies.get("select.active.organization")})</span>
      )}
    </React.Fragment>
  );
}
